import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useToastStore, type Toast as ToastData, type ToastTone } from '../stores/toastStore';

import { IconButton, cn } from './ui';

const AUTO_DISMISS_MS = 4500;

const TONE: Record<ToastTone, { icon: typeof CheckCircle2; className: string }> = {
  success: { icon: CheckCircle2, className: 'bg-success-soft text-success ring-success/20' },
  error: { icon: AlertCircle, className: 'bg-danger-soft text-danger ring-danger/20' },
  info: { icon: Info, className: 'bg-info-soft text-info ring-info/20' },
};

function ToastItem({ toast }: { toast: ToastData }) {
  const dismiss = useToastStore((s) => s.dismiss);
  useEffect(() => {
    const timer = setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toast.id, dismiss]);

  const { icon: Icon, className } = TONE[toast.tone];
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className="pointer-events-auto flex w-80 items-start gap-3 rounded-xl bg-surface p-3.5 ring-1 ring-hairline shadow-e3"
      role="status"
    >
      <span
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset [&_svg]:size-4',
          className,
        )}
      >
        <Icon />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{toast.title}</p>
        {toast.description && (
          <p className="mt-0.5 text-xs leading-snug text-foreground-muted">{toast.description}</p>
        )}
      </div>
      <IconButton label="Dismiss" size="sm" variant="ghost" onClick={() => dismiss(toast.id)}>
        <X />
      </IconButton>
    </motion.div>
  );
}

/**
 * Global toast outlet. Mounted once near the app root; renders via portal
 * in the bottom-end corner (logical, RTL-safe), stacking newest at the
 * bottom. Fed by the `toast` helper / toastStore.
 */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  return createPortal(
    <div
      // Portaled under the global dir="rtl"; the admin console is English, so
      // force LTR — keeps the stack pinned bottom-right (items-end = right).
      dir="ltr"
      className="pointer-events-none fixed inset-0 z-[60] flex flex-col items-end justify-end gap-2 p-4 sm:p-6"
      aria-live="polite"
      aria-relevant="additions"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
